import React, { useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { BatchItem } from "shared/api";
import { Page } from "shared/ui";
import { useTranslation } from "react-i18next";
import { useModal } from "shared/hooks";
import { noop } from "shared/helpers";
import * as Yup from "yup";
import { BatchesTable, AddBatchModal, EditBatchModal } from "./ui";
import { BatchFormValues } from "./types";

const initialValues: BatchFormValues = {
  name: "",
  description: undefined,
  orders: undefined,
  packagings: undefined,
  company: null,
};

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  company: Yup.number().required("Company is required").nullable(),
  description: Yup.string().nullable(),
  orders: Yup.array().of(Yup.number()).nullable(),
  packagings: Yup.array().of(Yup.number()).nullable(),
});

const BatchViewPage = () => {
  const { t } = useTranslation();
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const [visibleAddModal, showAddModal, hideAddModal] = useModal();
  const [visibleEditModal, showEditModal, hideEditModal] = useModal();

  const batchFormik = useFormik<BatchFormValues>({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,
    onSubmit: noop,
  });

  const handleEditBatch = (batch: BatchItem) => {
    setSelectedBatchId(batch.id);
    showEditModal();
  };

  const handleCloseEditModal = () => {
    setSelectedBatchId(null);
    batchFormik.resetForm();
    hideEditModal();
  };

  return (
    <FormikProvider value={batchFormik}>
      <Page
        title={t("sidebar.batchView")}
        buttonTitle="Add Batch"
        onClick={showAddModal}
      >
        <BatchesTable
          showEditModal={handleEditBatch}
          hasEditPermission
          hasDeletePermission
        />

        <AddBatchModal isOpenModal={visibleAddModal} hideModal={hideAddModal} />

        <EditBatchModal
          isOpenModal={visibleEditModal}
          hideModal={handleCloseEditModal}
          batchId={selectedBatchId}
        />
      </Page>
    </FormikProvider>
  );
};

export { BatchViewPage };
